import { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';

import {
  fetchDashboardFeatures,
  fetchFeatureFlagOverrides,
  fetchFeatureFlags
} from '../../api/feature-flags';
import { handleQueryError } from '../../utils';

const normalizeKeys = (keys) => {
  if (Array.isArray(keys)) return keys.filter(Boolean);
  return keys ? [keys] : undefined;
};

const useDashboardFeatures = ({ keys } = {}) => {
  const normalizedKeys = normalizeKeys(keys);
  const hasKeyFilter = keys !== undefined;
  const queryEnabled = !hasKeyFilter || normalizedKeys.length > 0;

  const { data, isLoading, isError, isFetching, error, refetch } = useQuery(
    ['dashboardFeatures', normalizedKeys?.join('|') || 'all'],
    () => fetchDashboardFeatures(normalizedKeys ? { keys: normalizedKeys } : undefined),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false, enabled: queryEnabled }
  );

  if (isError) handleQueryError(error, 'dashboard features');

  const features = data?.data?.features || [];

  return { features, isLoading, isFetching, isError, refetch };
};

// Fail-closed: returns false while loading, on error, or when the flag is absent.
const useDashboardFeatureEnabled = (key) => {
  const { features, isLoading, isError } = useDashboardFeatures({ keys: key ? [key] : [] });
  if (isLoading || isError || !key) return false;
  const flag = features.find((f) => f.key === key);
  return flag?.enabled === true;
};

const useFetchFeatureFlagEnvState = ({ flagKey, environment = 'live' }) => {
  const [currentState, setCurrentState] = useState(null);

  const { isLoading, isError, data, error } = useQuery(
    ['featureFlagDetail', flagKey, environment],
    () => fetchFeatureFlags({ search: flagKey, environment, limit: 1 }),
    { staleTime: 30 * 1000, enabled: Boolean(flagKey) }
  );

  if (isError) handleQueryError(error, 'feature flag state');

  useEffect(() => {
    if (data?.data) {
      const match = (data.data.data || []).find((f) => f.key === flagKey);
      setCurrentState(match?.current_state || null);
    }
  }, [data, flagKey]);

  return { currentState, isLoading };
};

const FLAG_LIST_LIMIT = 50;

const useFetchFeatureFlags = ({ environment = 'live' } = {}) => {
  const [search, setSearchState] = useState('');

  const { data, isLoading, isFetching, isRefetching, refetch, fetchNextPage } = useInfiniteQuery(
    ['featureFlags', environment, search],
    ({ pageParam = 0 }) =>
      fetchFeatureFlags({ search, limit: FLAG_LIST_LIMIT, offset: pageParam, environment }),
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage?.data?.meta?.hasNext ? allPages.length * FLAG_LIST_LIMIT : undefined,
      keepPreviousData: true,
      refetchOnMount: 'always',
      onError: (err) => handleQueryError(err, 'feature flags'),
      retry: (n, err) => n < 1 && (!err?.response?.status || err.response.status >= 500)
    }
  );

  const flags = (data?.pages || []).flatMap((p) => p?.data?.data || []);
  const lastPage = data?.pages?.[data.pages.length - 1];
  const meta = lastPage?.data?.meta || { hasNext: false, count: 0 };

  return {
    flags,
    meta,
    isLoading,
    isFetching,
    isRefetching,
    search,
    setSearch: setSearchState,
    loadMore: () => meta.hasNext && fetchNextPage(),
    refetch
  };
};

const OVERRIDES_LIST_LIMIT = 50;

const useFetchFeatureFlagOverrides = ({ merchantId, search, environment = 'live' } = {}) => {
  const { data, isLoading, isFetching, refetch, fetchNextPage } = useInfiniteQuery(
    ['featureFlagOverrides', environment, merchantId, search],
    ({ pageParam = 0 }) =>
      fetchFeatureFlagOverrides({
        merchantId,
        search,
        limit: OVERRIDES_LIST_LIMIT,
        offset: pageParam,
        environment
      }),
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage?.data?.meta?.hasNext ? allPages.length * OVERRIDES_LIST_LIMIT : undefined,
      keepPreviousData: true,
      refetchOnMount: 'always',
      onError: (err) => handleQueryError(err, 'feature flag overrides'),
      retry: (n, err) => n < 1 && (!err?.response?.status || err.response.status >= 500)
    }
  );

  const overrides = (data?.pages || []).flatMap((p) => p?.data?.overrides || []);
  const lastPage = data?.pages?.[data.pages.length - 1];
  const meta = lastPage?.data?.meta || { hasNext: false, count: 0 };

  return {
    overrides,
    meta,
    isLoading,
    isFetching,
    loadMore: () => meta.hasNext && fetchNextPage(),
    refetch
  };
};

export {
  useDashboardFeatureEnabled,
  useDashboardFeatures,
  useFetchFeatureFlagEnvState,
  useFetchFeatureFlagOverrides,
  useFetchFeatureFlags
};
